import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import { useEffect, useRef } from "react";
import { Alert } from "react-native";
import { useSelector } from "react-redux";
import api from "../../util/api";
import {
  putCartApplyCredits,
  putRetailerActivate,
  putRetailerChangeEmail,
  putRetailerDeactivate,
} from "../../util/apiNew";
import {
  deleteAdminRetailerAddress,
  deleteRetailerAddress,
  getApiError,
  isApiError,
  postAdminRetailerAddress,
  postCheckout,
  postOrders,
  postRetailerAddress,
  putAdminRetailerAddress,
  putCartAddress,
  putRetailerAddress,
} from "../../util/apiNew";
import { getProductInfoFromCartProduct, openCheckout } from "../../util/cart";
import { safeMap } from "../../util/functions";
import { stringNotEmpty } from "../../util/gFormatting";
import {
  tAddressPayload,
  tCartProduct,
  tCheckoutResponse,
  tDetailedCart,
  tLongAddress,
  tTransactionData,
  tUser,
  validate,
} from "tmt_api/src/types/types.validator";
import { handleErrorEvents } from "../errorHandler/actions";
import { tReduxAction } from "../types";
import { kDefaultUser } from "./reducer";
import Bugsnag from "@bugsnag/js";
import { useAppDispatch } from "src/redux";
import { tPromoCartConfirmVisible } from "../modals/types";
import { tRibbon } from "../errorHandler/types";
import { kRoute } from "src/kRoute";
import { openConfirmModal, openRetailerVendorSetupAccountModal } from "./modal-actions";

export const validateEmail = (myEmail: string): boolean => {
  return (
    stringNotEmpty(myEmail) &&
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gim.test(
      myEmail,
    )
  );
};

export const validatePassword = (myPassword: string): boolean => {
  return (
    stringNotEmpty(myPassword) && /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/.test(myPassword)
  );
};

export const createAccountWithEmailAndPassword = (myEmail: string, myPassword: string): tReduxAction => {
  return (dispatch) => {
    if (!validatePassword(myPassword)) {
      dispatch({
        type: "RIBBON",
        message: new Error(
          "Please enter a valid password!\n(i.e. At least:\n8 characters,\n1 uppercase letter,\n1 lowercase letter,\n1 number,\nand 1 special character: @$!%*#?&)",
        ),
      });
      return undefined;
    }
    return firebase
      .auth()
      .createUserWithEmailAndPassword(myEmail.trim(), myPassword)
      .then(() => {
        const currUser = firebase.auth().currentUser;
        if (currUser === undefined) {
          dispatch({
            type: "RIBBON",
            message: new Error("Error occurred while creating user. Please try again."),
          });
        }
        return currUser?.sendEmailVerification();
      })
      .catch((error: Error) => {
        Alert.alert("Failed to create your account!\nPlease try again later!");
        console.error(error);
      });
  };
};

/**
 * Login an existing user
 */
export const loginWithEmailAndPassword = (myEmail: string, myPassword: string): tReduxAction => {
  return (dispatch) => {
    dispatch({ type: "SET_IS_LOGGING_IN", isLoggingIn: true });
    return api
      .postNoAuth({ endpoint: "/users/checkVerification", body: { email: myEmail } })
      .then((response) => {
        if (!(response?.data ?? false)) {
          throw new Error("User not verified. Please verify your email before continuing.");
        }
        return firebase.auth().signInWithEmailAndPassword(myEmail.trim(), myPassword);
      })
      .then((userCredentials) => {
        return firebase.firestore().collection("users").doc(userCredentials.user?.uid).get();
      })
      .catch((error) => {
        dispatch({ type: "RIBBON", message: error });
        dispatch({
          type: "ERROR",
          error,
        });
      })
      .finally(() => {
        dispatch({ type: "SET_IS_LOGGING_IN", isLoggingIn: false });
      });
  };
};

/**
 * Logout the current user
 */
export const logout = (): tReduxAction => {
  return async (dispatch) => {
    await firebase.auth().signOut();
    dispatch({ type: "kUserLoggedOut" });
  };
};

/**
 * Send a reset password email to the given email.
 * @param myEmail
 */
export const sendResetPasswordEmail = (myEmail: string): tReduxAction => {
  return (dispatch) => {
    firebase
      .auth()
      .sendPasswordResetEmail(myEmail.trim())
      .then(() => {
        dispatch({
          type: "RIBBON",
          message: "Reset password email sent",
        });
      })
      .catch((error) => {
        dispatch({ type: "ERROR", error });
        dispatch({
          type: "RIBBON",
          message: new Error("Failed to send reset password email"),
        });
      });
  };
};

export const updateUserNewPassword = (myNewPassword: string): tReduxAction => {
  return (dispatch) => {
    const user = firebase.auth().currentUser;
    user
      ?.updatePassword(myNewPassword)
      .then(() => {
        dispatch({
          type: "RIBBON",
          message: "Reset password email sent",
        });
      })
      .catch((error) => {
        dispatch({ type: "ERROR", error });
        dispatch({
          type: "RIBBON",
          message: new Error("Failed to send reset password email"),
        });
      });
  };
};
export const openCheckoutAll = (): tReduxAction<Promise<tCheckoutResponse | undefined>> => {
  return async (dispatch) => {
    let response: tCheckoutResponse | undefined;
    let tokensResponse: tTransactionData["tokens"];
    let cartsIds: string[];

    const onSuccess = () => {
      tokensResponse.forEach((token) => {
        api.post({ endpoint: `/orders/${token.id}/${token.brandId}` as const, body: undefined }).catch((e) => {
          dispatch({
            type: "RIBBON",
            message: new Error(e?.response?.data ?? "An error occurred please try again."),
          });
        });
      });

      postOrders({ cartsIds });
    };

    try {
      response = await postCheckout();
    } catch (error) {
      const apiError = getApiError(error);
      if (apiError.code === "tmt.retailer.vendorSetup") {
        const confirmModalResponse = await dispatch(openConfirmModal());
        if (confirmModalResponse.status === "closed") return response;

        const vendorSetupModalResponse = await dispatch(openRetailerVendorSetupAccountModal());
        if (vendorSetupModalResponse.status === "closed") return response;

        response = await postCheckout();
      }
    }

    try {
      if (!response?.transactionData?.tokens) return response;

      const validTransactionData = validate("tTransactionData")(response.transactionData);
      tokensResponse = validTransactionData.tokens;
      cartsIds = response.cartsNotFinished;
      const tokens = validTransactionData.tokens.map((transactionData) => transactionData.token);

      openCheckout(tokens, onSuccess);
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error(getApiError(error).message) });
    }

    return response;
  };
};

export const openCheckoutSingle = (cart?: tDetailedCart): tReduxAction<Promise<tCheckoutResponse | undefined>> => {
  return async (dispatch) => {
    if (cart === undefined) return undefined;

    let response: tCheckoutResponse | undefined;
    let tokensResponse: tTransactionData["tokens"];

    const onSuccess = () => {
      tokensResponse.forEach((token) => {
        api
          .post({
            endpoint: `/orders/${token.id}/${cart.products[0].brandUid}` as const,
            body: undefined,
          })
          .catch(console.error);
      });
    };

    try {
      response = await postCheckout(cart.products[0].brandUid);
    } catch (error) {
      const apiError = getApiError(error);
      if (apiError.code === "tmt.retailer.vendorSetup") {
        const confirmModalResponse = await dispatch(openConfirmModal());
        if (confirmModalResponse.status === "closed") return response;

        const vendorSetupModalResponse = await dispatch(openRetailerVendorSetupAccountModal());
        if (vendorSetupModalResponse.status === "closed") return response;

        response = await postCheckout(cart.products[0].brandUid);
      }
    }

    try {
      if (!response?.transactionData?.tokens) return response;

      const validTransactionData = validate("tTransactionData")(response.transactionData);
      tokensResponse = validTransactionData.tokens;
      const tokens = validTransactionData.tokens.map((transactionData) => transactionData.token);

      openCheckout(tokens, onSuccess);
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error(getApiError(error).message) });
    }

    return response;
  };
};

export const removeProduct = (
  productActualSku: string,
  brandId: string,
  productId: string,
): tReduxAction<void, tPromoCartConfirmVisible | tRibbon> => {
  return (dispatch) => {
    api
      .post({ endpoint: `/cart/${brandId}/${productId}/${productActualSku}/remove`, body: undefined })
      .then(() => {
        dispatch({
          type: "RIBBON",
          message: "Successfully deleted product from cart",
        });
      })
      .catch(() => {
        dispatch({
          type: "RIBBON",
          message: new Error("Could not delete product from cart"),
        });
      });
  };
};

type tChangeQuantityProps = {
  productActualSku: string;
  brandId: string;
  productId: string;
  case_minimum: number;
  inventory?: number;
  currentQuantity: number;
  skipCheck?: boolean;
  isTester?: boolean;
  promoCartConfirmVisible?: boolean;
};
type tpChangeQuantity = ({
  productActualSku,
  brandId,
  productId,
  case_minimum,
  inventory,
  currentQuantity,
  skipCheck,
  isTester,
  promoCartConfirmVisible,
}: tChangeQuantityProps) => tReduxAction;

export const addQuantity = (cartProduct: tCartProduct): tReduxAction => {
  return () => {
    const { isTester } = getProductInfoFromCartProduct(cartProduct);
    return api.post({
      endpoint: `/cart/${cartProduct.brandUid}/${cartProduct.productId}/${cartProduct.actualSKU}/${
        isTester ? 1 : cartProduct.case_minimum
      }/add` as const,
      body: undefined,
    });
  };
};

export const addProductToCart = (params: {
  brandId: string;
  productId: string;
  productSku: string;
  quantity: string;
  isAddTester: boolean;
}): tReduxAction => {
  return (dispatch) => {
    return api
      .post({
        endpoint: `/cart/${params.brandId}/${params.productId}/${params.productSku}/${params.quantity}/add`,
        body: params.isAddTester ? { addTester: true } : undefined,
      })
      .then(() => {
        dispatch({ type: "RIBBON", message: "Case added" });
      });
  };
};

export const setQuantity: tpChangeQuantity = ({
  productActualSku,
  brandId,
  productId,
  case_minimum,
  inventory,
  currentQuantity,
  skipCheck,
  promoCartConfirmVisible,
}): tReduxAction<void, tPromoCartConfirmVisible | tRibbon> => {
  return (dispatch, getState) => {
    const rSellAsSet = getState().UserReducer.cart?.find((cart) => cart.brandUid === brandId)?.draftSellAsSet;
    if (!skipCheck && rSellAsSet && promoCartConfirmVisible === false) {
      dispatch({
        type: "PROMO_CART_MODAL_TOGGLE",
        promoCartConfirmVisible: true,
        promoCallback: () => {
          dispatch(
            setQuantity({
              productActualSku,
              brandId,
              productId,
              case_minimum,
              inventory,
              currentQuantity,
              skipCheck: true,
            }),
          );
        },
      });
      return Promise.resolve;
    }

    return api
      .post({
        endpoint: `/cart/${brandId}/${productId}/${productActualSku}/${currentQuantity}/add` as const,
        body: undefined,
      })
      .catch((error) => {
        dispatch({
          type: "RIBBON",
          message: new Error(
            isApiError(error) && error.response ? error.response?.data.message : "An error occurred please try again.",
          ),
        });
      });
  };
};
export const editPONumber = (cartId: string, myPONumber: string): tReduxAction => {
  return (dispatch) => {
    api
      .post({
        endpoint: `/cart/${cartId}/PO` as const,
        body: { pONumber: myPONumber },
      })
      .then(() => {
        dispatch({
          type: "RIBBON",
          message: "Successfully added PO number",
        });
      })
      .catch((e) => {
        dispatch({
          type: "RIBBON",
          message: new Error(e.data.message ?? "An error occurred please try again."),
        });
      });
  };
};
export const addPromoCode = (cartId: string, myPromoCode: string): tReduxAction => {
  return (dispatch) => {
    return api
      .post({
        endpoint: `/cart/${cartId}/promoCode`,
        body: { promotionCode: myPromoCode },
      })
      .then(() => {
        dispatch({
          type: "RIBBON",
          message: "Promotion applied to your cart.",
        });
      });
  };
};

export const applyRewardsCredit = (cartId: string): tReduxAction<Promise<Partial<tDetailedCart>>> => {
  return () => {
    return putCartApplyCredits(cartId);
  };
};

export const subtractQuantity = (cartProduct: tCartProduct): tReduxAction => {
  return () => {
    const { isTester } = getProductInfoFromCartProduct(cartProduct);
    const tempQuantity = isTester ? -1 : cartProduct.case_minimum * -1;
    return api.post({
      endpoint:
        `/cart/${cartProduct.brandUid}/${cartProduct.productId}/${cartProduct.actualSKU}/${tempQuantity}/add` as const,
      body: undefined,
    });
  };
};

export const updateUser = (myUser: tUser): void => {
  try {
    const validatedUser = validate("tUser")(myUser);
    firebase.firestore().collection("users").doc(myUser.uid).set(validatedUser);
  } catch (e) {
    console.error(e);
  }
};

export const useFirebaseAuth = (): void => {
  const setInitialCurrentRouteNameRef = useRef(false);
  const dispatch = useAppDispatch();
  const isLoggedIn = useSelector((state) => state.UserReducer.isLoggedIn);

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((firebaseUser) => {
      if (firebaseUser && isLoggedIn) {
        dispatch({ type: "SET_LOADING_USER", loading: false });
        return;
      }

      if (firebaseUser && !isLoggedIn) {
        (async () => {
          const user = await dispatch(fetchUserData(firebaseUser));
          if (!setInitialCurrentRouteNameRef.current && user) {
            setInitialCurrentRouteNameRef.current = true;
            dispatch({
              type: "SET_CURRENT_ROUTE_NAME",
              payload: user.accountType === "retailer" ? kRoute.P99 : kRoute.P02,
            });
          }
        })();
        return;
      }

      if (!firebaseUser) {
        dispatch(logout());
      }
    });
    return () => unsubscribe();
  }, [dispatch, isLoggedIn]);
};

const fetchUserData = ({ uid, email }: firebase.User): tReduxAction<Promise<tUser | undefined | void>> => {
  return async (dispatch, getState) => {
    const { profile } = getState().UserReducer;
    return (
      firebase
        .firestore()
        .collection("users")
        .doc(uid)
        // .onSnapshot((mySnapshot))
        .get()
        .then((userData) => {
          // TODO: why are we logging in a blank user?
          if (!userData.exists) {
            dispatch({
              type: "kUserLoggedIn",
              profile: { ...kDefaultUser, ...profile, email: email ?? kDefaultUser.email },
            });
            updateUser({ ...kDefaultUser, ...profile, email: email ?? kDefaultUser.email });
            throw new Error("Cannot find user data");
            // updateUser(profile)
          } else {
            const kValidator = validate("tUser");
            let aUserData: tUser;
            try {
              aUserData = kValidator(userData.data());
            } catch (error) {
              aUserData = kValidator({
                ...{
                  avatar:
                    "https://upload.wikimedia.org/wikipedia/commons/7/7f/Anglel_Bless_Legendary_Hills_1_m%C4%9Bs%C3%ADc_st%C3%A1%C5%99%C3%AD.jpg",
                  dateJoined: "",
                  messageThreadsIds: [],
                  phone: "",
                  role: "",
                },
                ...userData.data(),
              });
            }
            Bugsnag.setUser(aUserData.uid, aUserData.email, `${aUserData.lastName}, ${aUserData.firstName}`);
            if (window.FS) {
              window.FS.identify(aUserData.uid, {
                displayName: `${aUserData.lastName}, ${aUserData.firstName}`,
                email: aUserData.email,
              });
            }

            dispatch({
              type: "kUserLoggedIn",
              profile: aUserData,
            });

            dispatch({
              type: "SET_LOADING_USER",
              loading: false,
            });

            return aUserData;
          }
        })
        .catch((error: Error) => {
          dispatch({
            type: "ERROR",
            error,
          });
        })
    );
  };
};

export const addRetailerAddress = (
  payload: tAddressPayload,
  retailerId?: string,
): tReduxAction<Promise<tLongAddress[]>> => {
  return async (dispatch) => {
    try {
      const addresses = retailerId
        ? await postAdminRetailerAddress(retailerId, payload)
        : await postRetailerAddress(payload);
      dispatch({ type: "ADMIN_UPDATE_ADDRESSES", addresses });
      return addresses;
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error("Something wrong happened") });
      throw error;
    }
  };
};

export const updateRetailerAddress = (
  addressId: string,
  payload: tAddressPayload,
  retailerId?: string,
): tReduxAction<Promise<tLongAddress[]>> => {
  return async (dispatch) => {
    try {
      const addresses = retailerId
        ? await putAdminRetailerAddress(retailerId, addressId, payload)
        : await putRetailerAddress(addressId, payload);
      dispatch({ type: "ADMIN_UPDATE_ADDRESSES", addresses });
      return addresses;
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error("Something wrong happened") });
      throw error;
    }
  };
};

export const removeRetailerAddress = (
  addressId: string,
  retailerId?: string,
): tReduxAction<Promise<tLongAddress[]>> => {
  return async (dispatch) => {
    try {
      const addresses = retailerId
        ? await deleteAdminRetailerAddress(retailerId, addressId)
        : await deleteRetailerAddress(addressId);
      dispatch({ type: "ADMIN_UPDATE_ADDRESSES", addresses });
      return addresses;
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error("Something wrong happened") });
      throw error;
    }
  };
};

export const changeCartAddress = (cartId: string, data: tLongAddress): tReduxAction => {
  return async (dispatch) => {
    try {
      const payload: tAddressPayload = {
        id: data.id,
        isDefault: !!data.isDefault,
        name: data.name || "",
        phone: data.phone || "",
        address1: data.address1,
        address2: data.address2 || "",
        city: data.city,
        stateCode: data.state_or_province,
        zipCode: data.postal_code,
        countryCode: data.country_code,
      };
      await putCartAddress(cartId, payload);
      dispatch({ type: "RIBBON", message: "Address changed" });
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error("Something wrong happened") });
      throw error;
    }
  };
};

export const activateRetailer = (retailerId: string): tReduxAction => {
  return async (dispatch) => {
    try {
      await putRetailerActivate(retailerId);
      dispatch({ type: "RIBBON", message: "User activated" });
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error(getApiError(error).message) });
      throw error;
    }
  };
};

export const deactivateRetailer = (retailerId: string): tReduxAction => {
  return async (dispatch) => {
    try {
      await putRetailerDeactivate(retailerId);
      dispatch({ type: "RIBBON", message: "User deactivated" });
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error(getApiError(error).message) });
      throw error;
    }
  };
};

export const changeEmailRetailer = (retailerId: string, newEmail: string): tReduxAction => {
  return async (dispatch) => {
    try {
      await putRetailerChangeEmail(retailerId, newEmail);
      dispatch({ type: "RIBBON", message: "Email changed" });
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error(getApiError(error).message) });
      throw error;
    }
  };
};

export const useFetchCart = (): void => {
  const dispatch = useAppDispatch();
  const userId = useSelector(({ UserReducer }) => UserReducer.profile.uid);

  useEffect(() => {
    return firebase
      .firestore()
      .collection(`carts`)
      .where("userId", "==", userId)
      .orderBy("isConnected", "desc")
      .orderBy("name", "desc")
      .onSnapshot(
        (myCartSnapshot) => {
          if (myCartSnapshot.empty) {
            dispatch({
              type: "SET_CART",
              cart: [],
              numberOfCarts: 0,
            });
            return;
          }
          const mySnapshotCart: tDetailedCart[] = safeMap(myCartSnapshot.docs, validate("tDetailedCart"));
          dispatch({
            type: "SET_CART",
            cart: mySnapshotCart,
            numberOfCarts: mySnapshotCart.length,
          });
        },
        (error) => {
          dispatch(handleErrorEvents(error.message, "error", error));
        },
      );
  }, [dispatch, userId]);
};
